import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, Spinner, Alert, Nav } from 'react-bootstrap';
import updateService from '../services/updateService';

const Updates = () => {
  const [updates, setUpdates] = useState([]);
  const [filteredUpdates, setFilteredUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeFilter, setActiveFilter] = useState('ALL');

  const updateTypes = {
    ALL: 'All Updates',
    GENERAL: 'General',
    FEATURE: 'New Features',
    BUGFIX: 'Bug Fixes',
    MAINTENANCE: 'Maintenance',
    ANNOUNCEMENT: 'Announcements',
    SECURITY: 'Security'
  };

  const priorityColors = {
    LOW: 'secondary',
    MEDIUM: 'primary',
    HIGH: 'warning',
    CRITICAL: 'danger'
  };

  const typeColors = {
    GENERAL: 'secondary',
    FEATURE: 'success',
    BUGFIX: 'warning',
    MAINTENANCE: 'info',
    ANNOUNCEMENT: 'primary',
    SECURITY: 'danger'
  };

  useEffect(() => {
    loadUpdates();
  }, []);

  useEffect(() => {
    filterUpdates();
  }, [updates, activeFilter]);

  const loadUpdates = async () => {
    try {
      const response = await updateService.getPublishedUpdates();
      setUpdates(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading updates:', error);
      setError('Failed to load updates');
      setLoading(false);
    }
  };

  const filterUpdates = () => {
    if (activeFilter === 'ALL') {
      setFilteredUpdates(updates);
    } else {
      setFilteredUpdates(updates.filter(update => update.updateType === activeFilter));
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <h4 className="mt-3">Loading Updates...</h4>
      </Container>
    );
  }

  return (
    <Container className="mt-5">
      <Row>
        <Col>
          <h1 className="mb-4">
            <i className="fas fa-bell me-2"></i>
            Updates & Announcements
          </h1>
          {error && <Alert variant="danger">{error}</Alert>}
        </Col>
      </Row>

      {/* Filter Navigation */}
      <Row className="mb-4">
        <Col>
          <Nav variant="pills" className="flex-wrap">
            {Object.entries(updateTypes).map(([key, label]) => (
              <Nav.Item key={key} className="mb-2">
                <Nav.Link
                  active={activeFilter === key}
                  onClick={() => setActiveFilter(key)}
                  className="me-2"
                >
                  {label}
                  {key !== 'ALL' && (
                    <Badge 
                      bg="light" 
                      text="dark" 
                      className="ms-2"
                    >
                      {updates.filter(u => u.updateType === key).length}
                    </Badge>
                  )}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
        </Col>
      </Row>

      {filteredUpdates.length === 0 ? (
        <Row>
          <Col>
            <Alert variant="info">
              <h4>No updates available</h4>
              <p>
                {activeFilter === 'ALL' 
                  ? 'No updates have been published yet.' 
                  : `No ${updateTypes[activeFilter].toLowerCase()} updates available.`
                }
              </p>
            </Alert>
          </Col>
        </Row>
      ) : (
        <Row>
          {filteredUpdates.map((update) => (
            <Col key={update.id} lg={12} className="mb-4">
              <Card className="shadow-sm update-card">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div className="flex-grow-1">
                      <Card.Title className="h4 mb-2">{update.title}</Card.Title>
                      <div className="d-flex flex-wrap gap-2 mb-2">
                        <Badge bg={typeColors[update.updateType] || 'secondary'}>
                          <i className="fas fa-tag me-1"></i>
                          {update.updateType}
                        </Badge>
                        <Badge bg={priorityColors[update.priority] || 'secondary'}>
                          <i className="fas fa-exclamation-circle me-1"></i>
                          {update.priority} Priority
                        </Badge>
                        <Badge bg="light" text="dark">
                          <i className="fas fa-calendar me-1"></i>
                          {formatDate(update.publishDate)}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  {update.summary && (
                    <Card.Text className="text-muted mb-3 fs-6">
                      <strong>Summary:</strong> {update.summary}
                    </Card.Text>
                  )}

                  <Card.Text 
                    className="update-content"
                    dangerouslySetInnerHTML={{ 
                      __html: update.content.replace(/\n/g, '<br/>') 
                    }}
                  />
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <style jsx>{`
        .update-card {
          transition: transform 0.2s ease-in-out;
          border-left: 4px solid #007bff;
        }
        .update-card:hover {
          transform: translateY(-2px);
        }
        .update-content {
          line-height: 1.6;
        }
      `}</style>
    </Container>
  );
};

export default Updates;